import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment
} from '@mui/material';
import { ArrowBack, Phone } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useResponsive } from '../../hooks/useResponsive';
import { formatPhone } from '../../utils/phoneFormatter';

interface ExecutorLoginFormProps {
  onBack: () => void;
  onSuccess: () => void;
}

export const ExecutorLoginForm: React.FC<ExecutorLoginFormProps> = ({ onBack, onSuccess }) => {
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<'phone' | 'code'>('phone');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [challengeToken, setChallengeToken] = useState('');
  
  const { loginExecutor, verifyExecutor, clearError } = useAuth();
  const { isMobile } = useResponsive();

  const handlePhoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phone.trim()) return;

    setLoading(true);
    setError('');
    clearError();

    try {
      const result = await loginExecutor({ phone });
      
      if (result.needsVerification) {
        setChallengeToken(result.challengeToken || '');
        setStep('code');
      } else if (result.error) {
        setError(result.error);
      } else {
        onSuccess();
      }
    } catch (err: any) {
      setError(err.message || 'Ошибка отправки SMS');
    } finally {
      setLoading(false);
    }
  };

  const handleCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim()) return;

    setLoading(true);
    setError('');
    clearError();

    try {
      await verifyExecutor({ phone, code, challengeToken });
      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Неверный код');
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhone(e.target.value);
    setPhone(formatted);
  };

  const isPhoneValid = phone.replace(/\D/g, '').length === 11;
  const isCodeValid = code.length === 4;

  return (
    <Box>
      {/* Заголовок с кнопкой назад */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton 
          onClick={onBack}
          sx={{ mr: 1 }}
        >
          <ArrowBack />
        </IconButton>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            Вход для исполнителя
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {step === 'phone' 
              ? 'Введите номер телефона для входа через ФНС' 
              : 'Введите код из SMS'
            }
          </Typography>
        </Box>
      </Box>

      {/* Ошибка */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Форма ввода телефона */}
      {step === 'phone' && (
        <form onSubmit={handlePhoneSubmit}>
          <TextField
            fullWidth
            label="Номер телефона"
            value={phone}
            onChange={handlePhoneChange}
            placeholder="+7 (999) 999-99-99"
            disabled={loading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Phone />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 3 }}
            helperText="Номер должен быть зарегистрирован в системе 'Мой налог'"
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={!isPhoneValid || loading}
            sx={{
              height: isMobile ? 48 : 56,
              fontSize: isMobile ? '1rem' : '1.1rem',
              fontWeight: 'bold'
            }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Получить код'
            )}
          </Button>
        </form>
      )}

      {/* Форма ввода кода */}
      {step === 'code' && (
        <form onSubmit={handleCodeSubmit}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Код отправлен на номер {phone}
          </Typography>

          <TextField
            fullWidth
            label="Код из SMS"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 4))}
            placeholder="1234"
            disabled={loading}
            inputProps={{
              maxLength: 4,
              style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5em' }
            }}
            sx={{ mb: 3 }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={!isCodeValid || loading}
            sx={{
              height: isMobile ? 48 : 56,
              fontSize: isMobile ? '1rem' : '1.1rem',
              fontWeight: 'bold',
              mb: 2
            }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Войти'
            )}
          </Button>

          <Button
            fullWidth
            variant="text"
            onClick={() => setStep('phone')}
            disabled={loading}
          >
            Изменить номер телефона
          </Button>
        </form>
      )}

      {/* Информация о ФНС */}
      <Box sx={{ mt: 4, p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
          <strong>Что такое "Мой налог"?</strong><br />
          Это приложение ФНС России для самозанятых. 
          Для работы на платформе необходимо быть зарегистрированным самозанятым.
        </Typography>
      </Box>
    </Box>
  );
};
